import * as React from "react";
import {useEffect, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {AgGridReact} from "ag-grid-react";
import {ColDef, ExcelStyle, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams,} from "ag-grid-enterprise";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {FilterDef} from "../../../../../constants/FinanceModels";
import {STATE_ACTIVE_FILTERS, STATE_TABLE_UPDATING,} from "../../atoms/ToolsAtoms";
import {formatDateWithoutDay, formatDateWithoutTime} from "../../../../../utils/FormatUtils";
import {usePaymentToolsRepository} from "../../../../../api/repositories/PaymentToolsRepository";
import {getLocalGridState,} from "../../../../../utils/GridUtils";
import {STATE_SUMMARY_UPDATING} from "../../../../../constants/FinanceRecoil";
import AgGridCustomTable from "../../../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../../../util/component/EmptyState";
import * as Const from "../filters/ToolsConstants";
import {useAgGridLocale} from "../../../../../../../config/globalConfig";
import dayjs from "dayjs";
import {useLocation} from "react-router-dom";
import {Button} from "@mui/material";
import {FileDownloadRounded} from "@mui/icons-material";
import {ExportDataAsExcelBase} from "../../../../../utils/ExportDataAsExcelBase";


const ToolsTable = (props: any) => {
  const UNIQUE_ID = "fnn-payments-tools";

  const { t } = useTranslation("finance", { keyPrefix: "payments.tools" });
  const { getPaymentTools, getSummary } = usePaymentToolsRepository();

  const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
  const [updating, setUpdating] = useRecoilState(STATE_TABLE_UPDATING);

  const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
  const agGridLocale = useAgGridLocale();

  const gridRef = useRef<AgGridReact>(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get('customerId');

  const columnDefs : ColDef[] = [
    {
      field: "cardType",
      headerName: t("table.cardType"),
      minWidth: 200,
      unSortIcon: true,
      sortable: true,
      pinned: "left",
      lockPosition: "left",
      lockVisible: true,
      lockPinned: true,
      valueFormatter: (params: any) => params.value ? t(`types.${params.value}`): '',
    },
    {
      field: "owner",
      headerName: t("table.owner"),
      minWidth: 200,
      unSortIcon: true,
      sortable: true,
    },
    {
      field: "customerId",
      headerName: t("table.customerId"),
      minWidth: 200,
      unSortIcon: true,
      sortable: true,
    },
    {
      field: "issuer",
      headerName: t("table.issuer"),
      minWidth: 200,
      unSortIcon: true,
      sortable: true,
    },
    {
      field: "category",
      headerName: t("table.category"),
      minWidth: 200,
      unSortIcon: true,
      sortable: true,
      valueFormatter: (params: any) => params.value ? t(`categories.${params.value}`): '',
    },
    {
      field: "cardNetwork",
      headerName: t("table.cardNetwork"),
      minWidth: 200,
      unSortIcon: true,
      sortable: true,
    },
    {
      field: "countryOfIssueIsoA2",
      headerName: t("table.countryOfIssue"),
      minWidth: 200,
      unSortIcon: true,
      sortable: true,
      cellRenderer: (params: any) => params.value ? params.value + (params.data.countryOfIssueIsoName ? " - " + params.data.countryOfIssueIsoName : "") : "---"
    },
    {
      field: "expirationDate",
      headerName: t("table.expirationDate"),
      minWidth: 200,
      unSortIcon: true,
      sortable: true,
      valueFormatter: (params: any) => params?.value ? formatDateWithoutDay(params.value) : "",
    },
    {
      field: "pan",
      headerName: t("table.pan"),
      minWidth: 200,
      unSortIcon: true,
      sortable: true,
    },
    {
      field: "creationDate",
      headerName: t("table.creationDate"),
      minWidth: 200,
      unSortIcon: true,
      sortable: true,
      valueFormatter: (params: any) => params?.value ? formatDateWithoutTime(params.value) : "",
    },
    {
      field: "correlationName",
      headerName: t("table.correlationName"),
      minWidth: 200,
      unSortIcon: true,
      sortable: true,
      cellRenderer: (params: any) => Const.checkCorrelationName(params, t)
    },
    {
      field: "correlationId",
      headerName: t("table.correlationId"),
      minWidth: 200,
      unSortIcon: true,
      sortable: true,
      valueFormatter: params => Const.checkCorrelationID(params, t)
    },
    {
      field: "legalEntity", headerName: t("table.legalEntity"),
      minWidth: 180,
      unSortIcon: true, sortable: true,
    },
    {
      field: "dealer", headerName: t("table.dealer"),
      minWidth: 180,
      unSortIcon: true, sortable: true,
    },
  ];

  const gridOptions: GridOptions = {
    getRowId: (params: GetRowIdParams) => params.data["id"],
    defaultColDef: {
      sortable: true,
      resizable: true,
      wrapHeaderText: false,
    },
    noRowsOverlayComponentParams: {
      title: t("no-rows-overlay"),
    },
  };

  useEffect(() => {
    gridRef.current?.api?.setServerSideDatasource(
      createDatasource(filterDefs, gridRef.current.api)
    );
    setSummaryUpdating(true);
  }, [filterDefs]);

  useEffect(() => {
    if (updating) {
      gridRef.current?.api?.setServerSideDatasource(
        createDatasource(filterDefs, gridRef.current.api)
      );
      setUpdating(false);
      setSummaryUpdating(true);
    }
  }, [updating]);

  useEffect(() => {
    setSummaryUpdating(true);
  }, [agGridLocale]);

  const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
    return {
      getRows: async (params: IServerSideGetRowsParams) => {
        let startRow = params.request.startRow ? params.request.startRow : 0;
        let endRow = params.request.endRow ? params.request.endRow : 10;
        let sort =
          params.request.sortModel.length === 0
            ? null
            : params.request.sortModel[0].colId;
        let order =
          params.request.sortModel.length === 0
            ? null
            : params.request.sortModel[0].sort.toUpperCase();

        getPaymentTools(startRow, endRow - startRow, sort, order, filterDefs, customerId)
          .then((response) => {
            let result = response.result;

            if (result.totalElements === 0) {
              api.showNoRowsOverlay();
            } else {
              api.hideOverlay();
            }

            params.success({
              rowData: result.content,
              rowCount: result.totalElements,
            });
          })
          .catch(() => params.fail());
      },
    };
  };

  const noRowsOverlayComponent = useMemo<any>(() => {
    return EmptyState;
  }, []);

  const toolbar = {
    renderActions: () => (
        <>
          <Button fullWidth color={"accent"} variant="contained" component="label" onClick={exportAsExcel}>
            <FileDownloadRounded/> {t('exportPaymentTool')}
          </Button>
        </>
    )
  };

  function getExportableExcelColumns(): string[] {
    let keys: string[] = gridRef?.current?.columnApi.getAllDisplayedColumns().map(column => column.getColId()) as string[];

    return keys;
  }

  function exportAsExcel() {
    gridRef?.current?.api.exportDataAsExcel({
      ...ExportDataAsExcelBase,
      author: "Finance - Payments",
      fileName: `Tokenized Cards - ${dayjs().format("L")}.xlsx`,
      sheetName: `Tokenized Cards`,
      columnKeys: getExportableExcelColumns(),
      processCellCallback: (params) => {

        if (params.column.getColId() === 'creationDate') {
          return formatDateWithoutTime(params.value);
        }
        if (params.column.getColId() === 'expirationDate') {
          return formatDateWithoutDay(params.value);
        }
        if (params.column.getColId() === 'cardType') {
          let cardType: string = params.value;
          return cardType !== undefined ? t("types." + cardType) : "";
        }
        if (params.column.getColId() === 'category') {
          let category: string = params.value;
          return category !== undefined ? t("categories." + category) : "";
        }
        if (params.column.getColId() === 'status') {
          let status: string = params.value;
          return t("statuses." + status);
        }
        if (params.column.getColId() === 'countryOfIssueIsoA2') {
          return params.value ? params.value + (params.node?.data?.countryOfIssueIsoName ? " - " + params.node?.data.countryOfIssueIsoName : "") : "---"
        }
        if (params.column.getColId() === 'correlationName') {
          return Const.checkCorrelationNameExcel( params.node, t);
        }
        if (params.column.getColId() === 'correlationId') {
          return Const.checkCorrelationID(params.node, t)
        }

        return params.value;
      },
    });
  }

  const excelStyles: ExcelStyle[] = [
    {
      id: "typeString",
      dataType: "String"
    }
  ];


  return (
    <>
      <AgGridCustomTable
        toolbar={toolbar}
        rowModelType="serverSide"
        paginationPageSize={getLocalGridState(UNIQUE_ID).rows}
        serverSideInfiniteScroll
        gridRef={gridRef}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        rowSelection={"multiple"}
        excelStyles={excelStyles}
        suppressRowClickSelection
        noRowsOverlayComponent={noRowsOverlayComponent}
        onGridReady={() => {
          gridRef.current!.api.setServerSideDatasource(
            createDatasource(filterDefs, gridRef.current!.api)
          );

          let gridState = getLocalGridState(UNIQUE_ID).cols;
          if (gridState !== undefined) {
            gridRef.current!.columnApi.applyColumnState({
              state: gridState,
              applyOrder: true,
            });
          }
        }}
      ></AgGridCustomTable>
    </>
  );
};

export default ToolsTable;
